import { currentlyOpenLMDialogsAtom } from 'packages/lib/common-ui/log-miner-legacy-ui/log-miner-recoil';
import { useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';

interface UseDialogOpenStateReturns {
  isDialogOpen: (dialogName: string) => boolean;
  isAnyDialogOpen: () => boolean;
  setDialogOpenOrClosed: (dialogName: string, isOpen: boolean) => void;
}

/** It can be useful to keep track of a dialog's open/closed state globally, so other components can know whether to behave in a certain way,
 such as enable or disable their keyboard shortcuts. Use the returned setDialogOpenOrClosed() method much the same as you would use useState() to
 keep track of a dialog's open state. Use a unique string key for each dialog you keep track of with this.
 */
export const useDialogOpenState = (): UseDialogOpenStateReturns => {
  const [currentlyOpenDialogs, setCurrentlyOpenDialogs] = useAtom<
    Map<string, boolean>
  >(currentlyOpenLMDialogsAtom);

  function isDialogOpen(dialogName: string): boolean {
    return currentlyOpenDialogs.has(dialogName);
  }

  function isAnyDialogOpen(): boolean {
    return !!(currentlyOpenDialogs.size > 0);
  }

  // WARNING: Do NOT include this function in the dependency array of a useEffect or similar. This function CHANGES constantly, because
  // setCurrentlyOpenDialogs is always changing.
  function setDialogOpenOrClosed(dialogName: string, isOpen: boolean): void {
    setCurrentlyOpenDialogs((curOpenDialogs) => {
      const newCurrentlyOpenDialogs = new Map(curOpenDialogs);
      if (isOpen) {
        newCurrentlyOpenDialogs.set(dialogName, true);
      } else {
        newCurrentlyOpenDialogs.delete(dialogName);
      }

      return newCurrentlyOpenDialogs;
    });
  }

  return {
    isDialogOpen,
    isAnyDialogOpen,
    setDialogOpenOrClosed,
  };
};

export const useIsDialogOpen = (dialogName: string): boolean => {
  const currentlyOpenDialogs = useAtomValue<Map<string, boolean>>(
    currentlyOpenLMDialogsAtom
  );

  return useMemo(
    () => currentlyOpenDialogs.has(dialogName),
    [currentlyOpenDialogs, dialogName]
  );
};

export default useDialogOpenState;
